import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Snackbar,
  FormControlLabel,
  Checkbox,
  Link,
} from "@mui/material";
import { ArrowBack, Google, Apple } from "@mui/icons-material";
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSafeArea } from '../hooks/useSafeArea';
import { useKeyboardScroll } from '../hooks/useKeyboardScroll';
import { useNavigate } from 'react-router-dom';
import { SubscriptionProvider } from '../contexts/SubscriptionContext';

interface SignUpPageProps {
  onBack: () => void;
}

const SignUpPage: React.FC<SignUpPageProps> = ({ onBack }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'success'>('error');

  const { signUp, signInWithGoogle, signInWithApple } = useAuth();
  const { theme } = useTheme();
  const { bottom } = useSafeArea();
  const navigate = useNavigate();

  // Hook para scroll automático quando teclado aparecer (Capacitor)
  useKeyboardScroll();

  const phoneRegex = /^(\(?\d{2}\)?\s?)?\d{4,5}-?\d{4}$/;

  // Função para exibir mensagem no Snackbar
  const showSnackbar = (message: string, severity: 'error' | 'success' = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Função para fechar o Snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError("");
  };

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email ||
      !formData.phone || !formData.password || !formData.confirmPassword) {
      setError("Preencha todos os campos obrigatórios");
      return false;
    }

    if (!acceptPrivacyPolicy) {
      setError("Você deve aceitar a Política de Privacidade para continuar");
      return false;
    }

    if (!phoneRegex.test(formData.phone)) {
      setError("Formato de telefone inválido. Use: (11) 99999-9999 ou 11 99999-9999");
      return false;
    }

    if (formData.password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("As senhas não coincidem");
      return false;
    }

    // Campo de gênero agora é opcional - não precisa validar

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      const userData = {
        name: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        privacyPolicyAccepted: true,
        privacyPolicyAcceptedAt: new Date().toISOString(),
        privacyPolicyVersion: "1.0",
        // Inicializar com plano free
        subscriptionPlan: 'free' as const,
        subscriptionStatus: 'active' as const,
      };

      await signUp(formData.email, formData.password, userData);

      // Conta criada com sucesso - navegar direto pro dashboard
      const successMsg = "Conta criada com sucesso!";
      showSnackbar(successMsg, 'success');

      // Navegar direto pro dashboard (não volta pro login!)
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1500);
    } catch (error: any) {
      console.error('Erro ao criar conta:', error);

      let errorMessage;

      // Mapear códigos de erro para mensagens amigáveis
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = "Este email já está em uso";
          break;
        case 'phone-already-in-use':
          errorMessage = "Este telefone já está cadastrado";
          break;
        case 'auth/weak-password':
          errorMessage = "A senha deve ter pelo menos 6 caracteres";
          break;
        case 'auth/invalid-email':
          errorMessage = "Email inválido";
          break;
        default:
          errorMessage = "Erro ao criar conta. Verifique seus dados e tente novamente.";
      }

      // Exibir erro no Snackbar e no Alert
      showSnackbar(errorMessage, 'error');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    if (!acceptPrivacyPolicy) {
      const errorMsg = "Você deve aceitar a Política de Privacidade para continuar";
      setError(errorMsg);
      showSnackbar(errorMsg, 'error');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      showSnackbar('Conta criada com sucesso!', 'success');
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1000);
    } catch (err: any) {
      console.error('Erro Google SignUp:', err);
      let errorMessage = 'Falha ao criar conta com Google';

      if (err.code === 'google-user-not-found') {
        errorMessage = err.message;
      }

      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignUp = async () => {
    if (!acceptPrivacyPolicy) {
      const errorMsg = "Você deve aceitar a Política de Privacidade para continuar";
      setError(errorMsg);
      showSnackbar(errorMsg, 'error');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await signInWithApple();
      showSnackbar('Conta criada com sucesso!', 'success');
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1000);
    } catch (err: any) {
      console.error('Erro Apple SignUp:', err);
      let errorMessage = 'Falha ao criar conta com Apple';

      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };


  const handlePrivacyPolicyClick = () => {
    // Abrir política de privacidade em nova aba
    window.open('/privacy-policy', '_blank');
  };

  return (
    <SubscriptionProvider>
      <Box
        sx={{
          minHeight: "100vh",
          position: "relative",
          bgcolor: theme.colors.primary,
          overflow: "hidden",
        }}
      >
        {/* Header com botão voltar */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "20%",
            bgcolor: theme.colors.background,
            display: "flex",
            alignItems: "center",
            px: 3,
            zIndex: 3,
          }}
        >
          <IconButton onClick={onBack} sx={{ color: theme.colors.text.primary }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2, color: theme.colors.text.primary }}>
            Criar Conta
          </Typography>
        </Box>

        {/* Container do formulário */}
        <Box
          sx={{
            position: "absolute",
            left: 16,
            right: 16,
            top: "22%",
            bottom: 0,
            mx: "auto",
            maxWidth: 440,
            overflowY: "auto",
            paddingBottom: `${Math.max(bottom, 20)}px`,
            zIndex: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 3,
              borderRadius: 3,
              bgcolor: "#eef3f7",
            }}
          >
            {error && (
              <Alert
                severity={error.includes("sucesso") ? "success" : "error"}
                sx={{ mb: 2 }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}

            {/* Checkbox de aceite da política de privacidade */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptPrivacyPolicy}
                    onChange={(e) => setAcceptPrivacyPolicy(e.target.checked)}
                    disabled={loading}
                    sx={{
                      color: theme.colors.primary,
                      '&.Mui-checked': {
                        color: theme.colors.primary,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: theme.colors.text.primary }}>
                    Eu li e aceito a{' '}
                    <Link
                      component="button"
                      onClick={handlePrivacyPolicyClick}
                      sx={{
                        color: theme.colors.primary,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        '&:hover': {
                          color: theme.colors.secondary,
                        },
                      }}
                    >
                      Política de Privacidade
                    </Link>
                  </Typography>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </Box>

            {/* Botões de login social */}
            <Box sx={{ mb: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleGoogleSignUp}
                disabled={loading || !acceptPrivacyPolicy}
                startIcon={<Google />}
                sx={{
                  mb: 1.5,
                  borderColor: theme.colors.border,
                  color: theme.colors.text.primary,
                  "&:hover": {
                    borderColor: theme.colors.primary,
                  },
                  "&:disabled": {
                    opacity: 0.6,
                  },
                }}
              >
                Continuar com Google
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleAppleSignUp}
                disabled={loading || !acceptPrivacyPolicy}
                startIcon={<Apple />}
                sx={{
                  mb: 1,
                  borderColor: theme.colors.border,
                  backgroundColor: '#000',
                  color: '#fff',
                  "&:hover": {
                    borderColor: theme.colors.primary,
                    backgroundColor: '#333',
                  },
                  "&:disabled": {
                    opacity: 0.6,
                  },
                }}
              >
                Continuar com Apple
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }}>ou</Divider>

            <form onSubmit={handleSubmit}>
              {/* Campos do formulário */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Nome"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  disabled={loading}
                  size="small"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: theme.colors.background,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  placeholder="Sobrenome"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  disabled={loading}
                  size="small"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: theme.colors.background,
                    },
                  }}
                />
              </Box>

              <TextField
                fullWidth
                placeholder="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                disabled={loading}
                size="small"
                required
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: theme.colors.background,
                  },
                }}
              />

              <TextField
                fullWidth
                placeholder="Telefone: (11) 99999-9999"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                disabled={loading}
                size="small"
                required
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: theme.colors.background,
                  },
                }}
              />

              <TextField
                fullWidth
                placeholder="Senha"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                disabled={loading}
                size="small"
                required
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: theme.colors.background,
                  },
                }}
              />

              <TextField
                fullWidth
                placeholder="Confirmar Senha"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                disabled={loading}
                size="small"
                required
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: theme.colors.background,
                  },
                }}
              />

              <Button
                fullWidth
                type="submit"
                disabled={loading || !acceptPrivacyPolicy}
                sx={{
                  bgcolor: theme.colors.secondary,
                  color: theme.colors.text.contrast,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  py: 1.5,
                  "&:hover": {
                    bgcolor: theme.colors.secondary,
                    opacity: 0.9,
                  },
                  "&:disabled": {
                    opacity: 0.6,
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Criar Conta"
                )}
              </Button>
            </form>
          </Paper>

          {/* Snackbar para feedback */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbarSeverity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </SubscriptionProvider>
  );
};

export default SignUpPage;
